import React, { useState } from 'react';
import moment from 'moment';
import { TimeBox } from '../../../types/timeBox';
import { ScheduleItem } from '../../../types';
import { Event } from '../../../types/calendar-event';
import { 
  getTimeBoxesForDay, 
  getEventsForDay,
  isEvent,
  isTimeBox,
  getItemDisplayTitle,
  formatTimeRange,
  getEventStyleClasses,
  getTimeBoxStyleClasses
} from '../../../utils/calendarHelper';
import { MapPin } from 'lucide-react';
import DateRangeButton from '../../../components/common/DateRangeButton';
import { IdealWeek } from '../../../types/user';

interface MonthViewProps {
  date: Date;
  scheduleItems: ScheduleItem[];
  onSelectTimeBox: (item: ScheduleItem) => void;
  onSelectSlot: (start: Date, end: Date) => void;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onNavigateToday: () => void;
  onDateSelect: (date: Date) => void;
  /** Ideal Week 主題日設定 */
  idealWeek?: IdealWeek;
}

// 移除重複的輔助函數，統一使用 calendarHelper 中的函數

const MonthView: React.FC<MonthViewProps> = ({
  date,
  scheduleItems,
  onSelectTimeBox,
  onSelectSlot,
  onNavigatePrev,
  onNavigateNext,
  onNavigateToday,
  onDateSelect,
  idealWeek
}) => {
  // 統一使用 calendarHelper 中的類型判斷函數
  const timeBoxes = scheduleItems.filter(isTimeBox) as TimeBox[];
  const events = scheduleItems.filter(isEvent) as Event[];
  // Hover 狀態管理
  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  // 獲取當月的第一天
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  
  // 獲取當月的最後一天
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  
  // 計算當月的第一天是星期幾（0 = 星期日）
  const firstDayWeekday = firstDayOfMonth.getDay();
  
  // 計算上個月需要顯示的天數
  const daysFromPrevMonth = firstDayWeekday;
  
  // 計算本月的總天數
  const daysInMonth = lastDayOfMonth.getDate();
  
  // 計算日曆視圖需要的總格子數（6行 x 7列 = 42格）
  const totalCells = 6 * 7;
  
  // 計算下個月需要顯示的天數
  const daysFromNextMonth = totalCells - daysFromPrevMonth - daysInMonth;
  
  // 生成上個月需要顯示的日期
  const prevMonthDates = [];
  const prevMonthLastDay = new Date(date.getFullYear(), date.getMonth(), 0).getDate();
  for (let i = prevMonthLastDay - daysFromPrevMonth + 1; i <= prevMonthLastDay; i++) {
    const day = new Date(date.getFullYear(), date.getMonth() - 1, i);
    prevMonthDates.push(day);
  }
  
  // 生成當月的日期
  const currentMonthDates = [];
  for (let i = 1; i <= daysInMonth; i++) {
    const day = new Date(date.getFullYear(), date.getMonth(), i);
    currentMonthDates.push(day);
  }
  
  // 生成下個月需要顯示的日期
  const nextMonthDates = [];
  for (let i = 1; i <= daysFromNextMonth; i++) {
    const day = new Date(date.getFullYear(), date.getMonth() + 1, i);
    nextMonthDates.push(day);
  }
  
  // 合併所有日期
  const allDates = [...prevMonthDates, ...currentMonthDates, ...nextMonthDates];
  
  // 將日期分成每週（每7個一組）
  const weeks = [];
  for (let i = 0; i < allDates.length; i += 7) {
    weeks.push(allDates.slice(i, i + 7));
  }
  
  // 處理日期點擊，創建新的時間箱
  const handleDateClick = (day: Date) => {
    const now = new Date();
    const start = new Date(day);
    start.setHours(now.getHours(), 0, 0, 0);
    
    const end = new Date(start);
    end.setHours(now.getHours() + 1, 0, 0, 0);
    
    onSelectSlot(start, end);
  };

  // 處理滑鼠進入日期格子
  const handleMouseEnter = (day: Date, event: React.MouseEvent) => {
    const dayTimeBoxes = getTimeBoxesForDay(timeBoxes, day);
    const dayEvents = getEventsForDay(events, day);
    const allDayItems = [...dayTimeBoxes, ...dayEvents];
    if (allDayItems.length > 2) {
      const rect = event.currentTarget.getBoundingClientRect();
      setPopupPosition({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
      setHoveredDay(day);
    }
  };

  // 處理滑鼠離開
  const handleMouseLeave = () => {
    setHoveredDay(null);
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* 工具欄 */}
      <div className="flex items-center justify-between py-3 px-4 border-b border-[var(--border-default)]">
        <DateRangeButton
          dateText={moment(date).format('MMM YYYY')}
          currentDate={date}
          onDateSelect={onDateSelect}
          isMobile={false}
        />

        <div className="flex items-center space-x-2">
          <button
            onClick={onNavigatePrev}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Previous"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>
          
          <button
            onClick={onNavigateToday}
            className="px-3 py-1.5 bg-sky-100 text-sky-700 rounded-md hover:bg-sky-200 transition-colors text-sm font-medium"
          >
            Today
          </button>
          
          <button
            onClick={onNavigateNext}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Next"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* 星期標頭 - 使用 sticky 定位 */}
      <div className="grid grid-cols-7 border-b border-[var(--border-default)] sticky top-0 z-sticky bg-surface-page">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
          const theme = idealWeek?.[index as keyof IdealWeek];
          return (
            <div key={index} className="text-center py-2 font-medium text-gray-500">
              <div>{day}</div>
              {theme && (
                <div
                  className="text-[10px] text-sky-400 font-normal truncate px-0.5 leading-tight"
                  title={theme.description || undefined}
                >
                  {theme.title}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* 日曆主體 */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-7 grid-rows-6 h-full">
          {weeks.flat().map((day, index) => {
            const isCurrentMonth = day.getMonth() === date.getMonth();
            const isToday = day.toDateString() === new Date().toDateString();
            const dayTimeBoxes = getTimeBoxesForDay(timeBoxes, day);
            const dayEvents = getEventsForDay(events, day);
            const allDayItems = [...dayTimeBoxes, ...dayEvents].sort(
              (a, b) => a.startTime.getTime() - b.startTime.getTime()
            );
            
            return (
              <div 
                key={index}
                className={`border border-gray-100 p-1 h-28 overflow-hidden cursor-pointer
                  hover:bg-sky-500/5 transition-colors duration-200 ${
                  isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                }`}
                onClick={() => handleDateClick(day)}
                onMouseEnter={(e) => handleMouseEnter(day, e)}
                onMouseLeave={handleMouseLeave}
              >
                <div className={`text-xs font-semibold mb-1 ${
                  isCurrentMonth ? 'text-gray-800' : 'text-gray-400'
                } ${
                  isToday ? 'bg-sky-500 text-white rounded-full w-5 h-5 flex items-center justify-center' : ''
                }`} {...(isToday ? { 'aria-current': 'date' as const } : {})}>
                  {day.getDate()}
                </div>
                
                {/* 當天的項目列表 - 顯示前兩個 (TimeBox + Event) */}
                <div className="space-y-1">
                  {allDayItems.slice(0, 2).map((item, itemIndex) => {
                    const isItemEvent = isEvent(item);
                    const displayTime = isItemEvent && item.timeType === 'allday' 
                      ? '' 
                      : `${moment(item.startTime).format('HH:mm')} `;
                    
                    return (
                      <div
                        key={`${isItemEvent ? 'event' : 'timebox'}-${itemIndex}`}
                        className={`p-1 text-xs truncate cursor-pointer min-h-[20px] 
                          hover:scale-105 hover:shadow-sm transition-all duration-200 ${
                          isItemEvent 
                            ? getEventStyleClasses(item as Event)
                            : getTimeBoxStyleClasses(item as TimeBox)
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectTimeBox(item);
                        }}
                      >
                        {displayTime}{getItemDisplayTitle(item)}
                      </div>
                    );
                  })}
                  
                  {/* 如果有更多項目，顯示 "更多" 提示 */}
                  {allDayItems.length > 2 && (
                    <div className="text-xs text-gray-500">
                      +{allDayItems.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* 彈出層 - 顯示該日所有時間箱 */}
      {hoveredDay && (
        <div
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-sm z-tooltip"
          style={{
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y - 10}px`,
            transform: 'translateX(-50%) translateY(-100%)'
          }}
          onMouseEnter={() => setHoveredDay(hoveredDay)}
          onMouseLeave={handleMouseLeave}
        >
          <div className="text-sm font-semibold text-gray-800 mb-2">
            {moment(hoveredDay).format('YYYY/MM/DD ddd')}
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(() => {
              const dayTimeBoxes = getTimeBoxesForDay(timeBoxes, hoveredDay);
              const dayEvents = getEventsForDay(events, hoveredDay);
              const allDayItems = [...dayTimeBoxes, ...dayEvents].sort(
                (a, b) => a.startTime.getTime() - b.startTime.getTime()
              );
              
              return allDayItems.map((item, index) => {
                const isItemEvent = isEvent(item);
                return (
                  <div
                    key={`popup-${isItemEvent ? 'event' : 'timebox'}-${index}`}
                    className={`p-2 text-xs cursor-pointer hover:opacity-80 transition-opacity ${
                      isItemEvent 
                        ? getEventStyleClasses(item as Event)
                        : getTimeBoxStyleClasses(item as TimeBox)
                    }`}
                    onClick={() => onSelectTimeBox(item)}
                  >
                    <div className="font-medium">{getItemDisplayTitle(item)}</div>
                    <div className="text-gray-600">
                      {formatTimeRange(item)}
                    </div>
                    {item.location && (
                      <div className="text-gray-600"><MapPin className="inline w-3 h-3 mr-1" /> {item.location}</div>
                    )}
                    {isItemEvent && (
                      <div className="text-xs text-gray-500 mt-1">Event</div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthView;